import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import BookService from '../services/BookService';
import './EnhancedAddBookModal.css';

function EnhancedAddBookModal({ onClose, onBookAdded }) {
  console.log('EnhancedAddBookModal rendered');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedBook, setSelectedBook] = useState(null);
  const [readingStatus, setReadingStatus] = useState('toRead');
  const [searchMode, setSearchMode] = useState(true); // true for search, false for manual
  const [isbnQuery, setIsbnQuery] = useState('');
  
  // Manual entry fields
  const [manualTitle, setManualTitle] = useState('');
  const [manualAuthor, setManualAuthor] = useState('');
  
  const { currentUser } = useAuth();

  // Debounced search function (matching iOS 500ms delay)
  const debouncedSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const results = await BookService.searchGoogleBooks(query);
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      setError('Failed to search books. Please try again.');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (searchMode && searchQuery) {
      const timeoutId = setTimeout(() => {
        debouncedSearch(searchQuery);
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery, searchMode, debouncedSearch]);

  const handleISBNSearch = async () => {
    if (!isbnQuery.trim()) return;

    try {
      setLoading(true);
      setError('');
      const book = await BookService.searchByISBN(isbnQuery.trim());
      
      if (book) {
        setSearchResults([book]);
      } else {
        setError('No book found with that ISBN');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('ISBN search error:', error);
      setError('Failed to search by ISBN. Please try again.');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBookSelect = (book) => {
    setSelectedBook(book);
    setError('');
  };

  const handleAddBook = async () => {
    if (!selectedBook && !manualTitle.trim()) {
      setError('Please select a book or enter book details');
      return;
    }

    try {
      setLoading(true);
      setError('');

      let bookData;
      
      if (selectedBook) {
        // Adding from Google Books search
        bookData = BookService.convertGoogleBookToBook(selectedBook, currentUser.uid);
        bookData.readingStatus = readingStatus;
      } else {
        // Manual entry
        bookData = {
          userId: currentUser.uid,
          title: manualTitle.trim(),
          authors: manualAuthor.trim() ? [manualAuthor.trim()] : ['Unknown Author'],
          description: '',
          imageUrl: null,
          readingStatus: readingStatus,
          pageCount: null,
          categories: [],
          publishedDate: '',
          publisher: '',
          language: 'en',
          isbn: null,
          googleBooksId: null,
          createdAt: new Date(),
          updatedAt: new Date(),
          rating: null,
          mood: null
        };
      }

      await BookService.addBook(currentUser.uid, bookData);
      onBookAdded();
      onClose();
    } catch (error) {
      console.error('Error adding book:', error);
      if (error.message.includes('already exists')) {
        setError(error.message);
      } else {
        setError('Failed to add book. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const BookSearchResult = ({ book }) => {
    const { volumeInfo } = book;
    const isSelected = selectedBook?.id === book.id;

    return (
      <div 
        className={`book-result ${isSelected ? 'selected' : ''}`}
        onClick={() => handleBookSelect(book)}
      >
        <div className="book-result-content">
          {volumeInfo.imageLinks?.thumbnail && (
            <img 
              src={volumeInfo.imageLinks.thumbnail} 
              alt={volumeInfo.title}
              className="book-thumbnail"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
          <div className="book-details">
            <h4>{volumeInfo.title}</h4>
            <p className="book-authors">
              {volumeInfo.authors?.join(', ') || 'Unknown Author'}
            </p>
            {volumeInfo.publishedDate && (
              <p className="book-year">{volumeInfo.publishedDate.split('-')[0]}</p>
            )}
            {volumeInfo.pageCount && (
              <p className="book-pages">{volumeInfo.pageCount} pages</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="enhanced-modal-content">
        <div className="modal-header">
          <h2>📚 Add New Book</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="mode-toggle">
          <button 
            className={`mode-btn ${searchMode ? 'active' : ''}`}
            onClick={() => {setSearchMode(true); setError(''); setSelectedBook(null);}}
          >
            🔍 Search Books
          </button>
          <button 
            className={`mode-btn ${!searchMode ? 'active' : ''}`}
            onClick={() => {setSearchMode(false); setError(''); setSearchResults([]);}}
          >
            ✏️ Manual Entry
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {searchMode ? (
          <div className="search-section">
            <div className="search-input-group">
              <input
                type="text"
                placeholder="Search for books by title, author, or keyword..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="isbn-search">
              <div className="isbn-input-group">
                <input
                  type="text"
                  placeholder="Or enter ISBN..."
                  value={isbnQuery}
                  onChange={(e) => setIsbnQuery(e.target.value)}
                  className="isbn-input"
                />
                <button 
                  onClick={handleISBNSearch}
                  className="isbn-search-btn"
                  disabled={loading}
                >
                  Search ISBN
                </button>
              </div>
            </div>

            <div className="search-results">
              {loading && (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Searching books...</p>
                </div>
              )}

              {searchResults.map((book) => (
                <BookSearchResult key={book.id} book={book} />
              ))}

              {!loading && searchQuery && searchResults.length === 0 && (
                <div className="no-results">
                  <p>No books found for "{searchQuery}"</p>
                  <p>Try a different search term or use manual entry.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="manual-section">
            <div className="form-group">
              <label htmlFor="manual-title">Book Title *</label>
              <input
                type="text"
                id="manual-title"
                value={manualTitle}
                onChange={(e) => setManualTitle(e.target.value)}
                placeholder="Enter book title"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="manual-author">Author</label>
              <input
                type="text"
                id="manual-author"
                value={manualAuthor}
                onChange={(e) => setManualAuthor(e.target.value)}
                placeholder="Enter author name"
                className="form-input"
              />
            </div>
          </div>
        )}

        {(selectedBook || (!searchMode && manualTitle.trim())) && (
          <div className="status-section">
            <div className="form-group">
              <label htmlFor="reading-status">Reading Status</label>
              <select
                id="reading-status"
                value={readingStatus}
                onChange={(e) => setReadingStatus(e.target.value)}
                className="form-select"
              >
                <option value="toRead">📚 To Read</option>
                <option value="reading">📖 Currently Reading</option>
                <option value="finished">✅ Finished</option>
              </select>
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button 
            onClick={handleAddBook}
            className="btn btn-primary"
            disabled={loading || (!selectedBook && (!manualTitle.trim() || searchMode))}
          >
            {loading ? 'Adding...' : 'Add Book'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default EnhancedAddBookModal;